/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

import { SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

export const DEFAULT_ASSET_IDENTIFIER_LABEL = 'Asset Identifier';
export const DEFAULT_EQUIPMENT_IDENTIFIER_LABEL = 'Equipment Identifier';
export const DEFAULT_REQUEST_NUMBER_LABEL = `${SERVICE_REQUEST_LABEL.singular} Number`;
export const DEFAULT_CUSTOMER_NAME_LABEL = 'Customer Name';

/**
 * A type's `code` is only unique among *active* rows for a sub-account —
 * deactivating one and creating another with the same code is allowed, so
 * the same code can legitimately match several historical records. Whenever
 * that happens, the most recently created one is the one that reflects the
 * key's current meaning and should win.
 */
const latestOf = (candidates) =>
  candidates.reduce((latest, t) => (new Date(t.createdAt) > new Date(latest.createdAt) ? t : latest));

/**
 * Finds the latest type record matching `code`. Types are platform-level
 * (globally unique among active rows), so a code can only match more than
 * one record when older, deactivated rows share it with the current one —
 * `latestOf` picks the current one in that case.
 */
export const findLatestTypeByCode = (code, types) => {
  const matches = (types || []).filter((t) => t.code === code);
  return matches.length ? latestOf(matches) : undefined;
};

/**
 * Collapses a types list down to one entry per code, keeping the most
 * recently created record for each — for building dropdown/filter option
 * lists where a repeatedly deactivated-and-recreated code must appear once.
 */
export const dedupeTypesByCode = (types) => {
  const latestByCode = new Map();
  (types || []).forEach((t) => {
    const existing = latestByCode.get(t.code);
    if (!existing || new Date(t.createdAt) > new Date(existing.createdAt)) {
      latestByCode.set(t.code, t);
    }
  });
  return [...latestByCode.values()];
};

const resolveLabel = (code, types, field, fallback) => {
  const label = findLatestTypeByCode(code, types)?.[field];
  if (typeof label !== 'string') {
    return fallback;
  }

  const trimmed = label.trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

export const resolveAssetIdentifierLabel = (code, types) =>
  resolveLabel(code, types, 'assetIdentifierLabel', DEFAULT_ASSET_IDENTIFIER_LABEL);

export const resolveEquipmentIdentifierLabel = (code, types) =>
  resolveLabel(code, types, 'equipmentIdentifierLabel', DEFAULT_EQUIPMENT_IDENTIFIER_LABEL);

export const resolveRequestNumberLabel = (code, types) =>
  resolveLabel(code, types, 'requestNumberLabel', DEFAULT_REQUEST_NUMBER_LABEL);

export const resolveCustomerNameLabel = (code, types) =>
  resolveLabel(code, types, 'customerNameLabel', DEFAULT_CUSTOMER_NAME_LABEL);

export const resolveTypeName = (code, types) =>
  findLatestTypeByCode(code, types)?.name || code;
